/**
 * ASCEND INVICTUS — impact émotionnel : 8 familles, 36 états.
 *
 * Un état n'appartient qu'à une seule famille. La famille porte toute la présentation
 * (image, icône, accent, lumière, ton, pensée du jour, intention, routines) ; l'état ne
 * porte que le libellé déclaré par l'utilisateur. Le mode de contexte reste seul maître
 * de la logique fonctionnelle — voir DayMode dans routine/enums.ts.
 */
export const EmotionalFamily = {
  rayonnement: 'rayonnement',
  serenite: 'serenite',
  pressionPeur: 'pression-peur',
  friction: 'friction',
  vulnerabilite: 'vulnerabilite',
  reparation: 'reparation',
  recentrage: 'recentrage',
  coupDur: 'coup-dur',
} as const;

export type EmotionalFamily = (typeof EmotionalFamily)[keyof typeof EmotionalFamily];

export const EmotionalState = {
  joie: 'joie',
  accomplissement: 'accomplissement',
  serenite: 'serenite',
  sousPression: 'sous-pression',
  stress: 'stress',
  anxiete: 'anxiete',
  inquietude: 'inquietude',
  incertitude: 'incertitude',
  apprehension: 'apprehension',
  colere: 'colere',
  frustration: 'frustration',
  irritation: 'irritation',
  impatience: 'impatience',
  injustice: 'injustice',
  conflitMineur: 'conflit-mineur',
  solitude: 'solitude',
  rejet: 'rejet',
  honte: 'honte',
  culpabilite: 'culpabilite',
  douteProfond: 'doute-profond',
  insuffisance: 'insuffisance',
  malade: 'malade',
  epuise: 'epuise',
  convalescence: 'convalescence',
  baissePhysique: 'baisse-physique',
  echec: 'echec',
  deception: 'deception',
  perteDeReperes: 'perte-de-reperes',
  confusion: 'confusion',
  transitionInterieure: 'transition-interieure',
  douteDirection: 'doute-direction',
  rupture: 'rupture',
  deuil: 'deuil',
  moralBas: 'moral-bas',
  mauvaiseNouvelle: 'mauvaise-nouvelle',
  chocPersonnel: 'choc-personnel',
} as const;

export type EmotionalState = (typeof EmotionalState)[keyof typeof EmotionalState];

/**
 * TABLE ÉTAT → FAMILLE — référence unique du système (miroir de la donnée validée du
 * design system). Ne la dupliquez pas ailleurs.
 */
export const STATE_FAMILY: Readonly<Record<EmotionalState, EmotionalFamily>> = {
  [EmotionalState.joie]: EmotionalFamily.rayonnement,
  [EmotionalState.accomplissement]: EmotionalFamily.rayonnement,
  [EmotionalState.serenite]: EmotionalFamily.serenite,
  [EmotionalState.sousPression]: EmotionalFamily.pressionPeur,
  [EmotionalState.stress]: EmotionalFamily.pressionPeur,
  [EmotionalState.anxiete]: EmotionalFamily.pressionPeur,
  [EmotionalState.inquietude]: EmotionalFamily.pressionPeur,
  [EmotionalState.incertitude]: EmotionalFamily.pressionPeur,
  [EmotionalState.apprehension]: EmotionalFamily.pressionPeur,
  [EmotionalState.colere]: EmotionalFamily.friction,
  [EmotionalState.frustration]: EmotionalFamily.friction,
  [EmotionalState.irritation]: EmotionalFamily.friction,
  [EmotionalState.impatience]: EmotionalFamily.friction,
  [EmotionalState.injustice]: EmotionalFamily.friction,
  [EmotionalState.conflitMineur]: EmotionalFamily.friction,
  [EmotionalState.solitude]: EmotionalFamily.vulnerabilite,
  [EmotionalState.rejet]: EmotionalFamily.vulnerabilite,
  [EmotionalState.honte]: EmotionalFamily.vulnerabilite,
  [EmotionalState.culpabilite]: EmotionalFamily.vulnerabilite,
  [EmotionalState.douteProfond]: EmotionalFamily.vulnerabilite,
  [EmotionalState.insuffisance]: EmotionalFamily.vulnerabilite,
  [EmotionalState.malade]: EmotionalFamily.reparation,
  [EmotionalState.epuise]: EmotionalFamily.reparation,
  [EmotionalState.convalescence]: EmotionalFamily.reparation,
  [EmotionalState.baissePhysique]: EmotionalFamily.reparation,
  [EmotionalState.echec]: EmotionalFamily.recentrage,
  [EmotionalState.deception]: EmotionalFamily.recentrage,
  [EmotionalState.perteDeReperes]: EmotionalFamily.recentrage,
  [EmotionalState.confusion]: EmotionalFamily.recentrage,
  [EmotionalState.transitionInterieure]: EmotionalFamily.recentrage,
  [EmotionalState.douteDirection]: EmotionalFamily.recentrage,
  [EmotionalState.rupture]: EmotionalFamily.coupDur,
  [EmotionalState.deuil]: EmotionalFamily.coupDur,
  [EmotionalState.moralBas]: EmotionalFamily.coupDur,
  [EmotionalState.mauvaiseNouvelle]: EmotionalFamily.coupDur,
  [EmotionalState.chocPersonnel]: EmotionalFamily.coupDur,
};

export function familyOfState(state: EmotionalState): EmotionalFamily {
  return STATE_FAMILY[state];
}

export function statesOfFamily(family: EmotionalFamily): readonly EmotionalState[] {
  return (Object.keys(STATE_FAMILY) as EmotionalState[]).filter(
    (state) => STATE_FAMILY[state] === family,
  );
}
